import { useState } from 'react';
import BootstrapTable from 'react-bootstrap/Table';

import { KubeCluster, KubeController } from 'common/graphqlTypes';
import ELEMENT, { createTestId } from 'common/element';

import Link from 'components/common/Link/Link';
import Button from 'components/common/Button/Button';

import LogOverviewClusterTableRow from './LogOverviewClusterTableRow';
import { createOrchestrationLink } from './LogOverviewPage.functions';

import './LogOverviewClusterTable.scss';
// TODO: remove Sources.scss
import './Sources.scss';

// Number of controllers to show per cluster. restare show when user clicks more button.
const LIMIT_CONTROLLER_SHOWN_PER_CLUSTER = 6;
// Threshold for hiding controllers. beyond this number we hide controllers
const TRUNCATE_CONTROLLERS_SHOWN_THRESHOLD = 10;

/**
 * Get list of controller that should be shown.
 * @param {[controller]} controllers - list of all controller
 * @param {Boolean} showAll - if true we show all controllers.
 */
const getShownControllers = (controllers: KubeController[], showAll: boolean): KubeController[] => {
  if (controllers.length <= TRUNCATE_CONTROLLERS_SHOWN_THRESHOLD || showAll) {
    return controllers;
  }
  return controllers.slice(0, LIMIT_CONTROLLER_SHOWN_PER_CLUSTER);
};

/**
 * Controller row for a cluster.
 * @param {object} cluster - cluster object
 */

type LogOverviewClusterTableProps = {
  cluster: KubeCluster;
  index: number;
};

function LogOverviewClusterTable({ cluster, index }: LogOverviewClusterTableProps): JSX.Element {
  const [allClustersShown, setAllClustersShown] = useState(false);
  const controllers = cluster.controllers;
  // Sort controllers in alphabetical order
  controllers.sort((a, b) => a.name.localeCompare(b.name));
  const shownControllers = getShownControllers(controllers, allClustersShown).filter(
    (controller) => controller.name.length > 0,
  );
  let showAllButton;
  if (cluster.controllers.length > TRUNCATE_CONTROLLERS_SHOWN_THRESHOLD) {
    const showControllersTestId = createTestId(ELEMENT.overviewShowControllers, index);
    showAllButton = (
      <tr className="LogOverviewSourcesRow--ClearBackground">
        <td colSpan={4}>
          <Button
            size="sm"
            className="LogOverviewSourcesRow__ShowMoreButton"
            onClick={(): void => {
              setAllClustersShown(!allClustersShown);
            }}
            testId={showControllersTestId}
          >
            {allClustersShown ? 'Show Less' : `Show All (${cluster.controllers.length})`}
          </Button>
        </td>
      </tr>
    );
  }

  const hideDashboardColumn = !cluster.dashboard?.name;

  const renderRows = (): JSX.Element[] =>
    shownControllers.map((controller, controllerIndex) => (
      <LogOverviewClusterTableRow
        key={controller.name}
        cluster={cluster}
        controller={controller}
        hideDashboardColumn={hideDashboardColumn}
        clusterIndex={index}
        controllerIndex={controllerIndex}
      />
    ));

  const resultRows =
    cluster.controllers.length === 0 ? (
      <tbody>
        <tr>
          <td>There are no results to show.</td>
        </tr>
      </tbody>
    ) : (
      <tbody className="SourcesTable__TableBody">
        {renderRows()}
        {showAllButton}
      </tbody>
    );

  const clusterLink = createOrchestrationLink('', cluster.name, null, null);
  const clusterTestId = createTestId(ELEMENT.overviewClusterName, index);

  return (
    <div className="LogOverviewClusterTable">
      <h3>
        <Link className="LogOverviewClusterTable__ClusterLink" href={clusterLink} testId={clusterTestId}>
          {cluster.name}
        </Link>
      </h3>
      <BootstrapTable>
        <thead>
          <tr className="LogOverviewClusterTable__HeaderRow">
            <td className="SourcesTable__ColumnHeader">Controllers</td>
            <td className="SourcesTable__ColumnHeader">Search Logs</td>
            {hideDashboardColumn ? null : <td className="SourcesTable__ColumnHeader">Dashboards</td>}
            <td className="SourcesTable__ColumnHeader">Labels</td>
          </tr>
        </thead>
        {resultRows}
      </BootstrapTable>
    </div>
  );
}

export default LogOverviewClusterTable;
